/**
 * Send Test — manually queue a notification. Source/template/product/locale
 * (from live APIs), channel pills gated by /health activeChannels, JSON editor
 * with live validity, fallback-rule builder, response banners. Ported 1:1.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, StyleSheet, Modal } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import { useToast } from '../components/toast';
import Icon, { IconName } from '../components/notif/Icon';
import { Copyable } from '../components/notif/badges';
import { Banner, Btn } from '../components/notif/ui';
import { sendNotification, getLocales, getTemplates, NotifyPayload } from '../services/notification/notify';
import { getAllUsage } from '../services/notification/usage';
import { getHealth } from '../services/notification/health';
import type { ChannelType } from '../schema/notification';
import type { FallbackRuleForm } from '../schema/rules/fallbackRule';
import { toApiFallback } from '../schema/rules/fallbackRule';
import { useT } from '../i18n/useT';

const LOCALE_LABELS: Record<string, string> = { en: 'English', ta: 'Tamil', hi: 'Hindi' };
const FALLBACK_LOCALES = ['en', 'ta', 'hi'];
const FALLBACK_PRODUCTS = ['works', 'crm', 'cal', 'tickets', 'catalog', 'chat', 'voice', 'backbone', 'notification'];
const CHANNELS: Array<{ key: ChannelType; icon: IconName }> = [
  { key: 'email', icon: 'mail' }, { key: 'whatsapp', icon: 'message' }, { key: 'sms', icon: 'phone' }, { key: 'push', icon: 'bell' },
];
const INITIAL_JSON = `{
  "customerName": "Deepak Kumar",
  "projectName": "Sample Project",
  "amountPaid": 4200,
  "totalAmount": 12000,
  "balanceDue": 7800,
  "paymentDate": "2026-06-15",
  "currency": "INR"
}`;

const Field: React.FC<{ label: string; required?: boolean; hint?: string; hintError?: boolean; children: React.ReactNode }> = ({ label, required, hint, hintError, children }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={{ marginBottom: 14 }}>
      <Text style={{ color: colors.ink2, fontSize: 12, fontWeight: '600', marginBottom: 5 }}>{label}{required && <Text style={{ color: colors.danger }}> *</Text>}</Text>
      {children}
      {!!hint && <Text style={{ color: hintError ? colors.danger : colors.ink4, fontSize: 11, marginTop: 4 }}>{hint}</Text>}
    </View>
  );
};

/**
 * Tiny select (RN has no native <select>). The open menu renders inside a
 * Modal so it escapes every parent stacking context — it can never be painted
 * over by sibling fields, and only one can be open at a time. A transparent
 * scrim closes it on any outside tap. The menu is anchored under the trigger
 * via measureInWindow.
 */
const Selectish: React.FC<{ value: string; onChange: (v: string) => void; options: string[]; labelFor?: (v: string) => string; width?: number }> = ({ value, onChange, options, labelFor, width }) => {
  const { theme: { colors } } = useTheme();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number } | null>(null);
  const triggerRef = useRef<View>(null);

  const openMenu = () => {
    // Measure the trigger's viewport position so the menu can anchor beneath it.
    triggerRef.current?.measureInWindow((x, y, w, h) => {
      setAnchor({ x, y: y + h + 2, w });
      setOpen(true);
    });
  };

  return (
    <View style={{ width }}>
      <Pressable ref={triggerRef} onPress={() => (open ? setOpen(false) : openMenu())} style={[ss.input, { borderColor: colors.line, backgroundColor: colors.paper, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
        <Text style={{ color: colors.ink, fontSize: 13 }} numberOfLines={1}>{labelFor ? labelFor(value) : value}</Text>
        <Icon name="chevronDown" size={12} color={colors.ink3} />
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        {/* Scrim: any outside tap closes the menu. */}
        <Pressable style={{ flex: 1 }} onPress={() => setOpen(false)}>
          {anchor && (
            <View
              style={[ss.dropdown, { backgroundColor: colors.card, borderColor: colors.line, position: 'absolute', top: anchor.y, left: anchor.x, width: anchor.w }]}
              // Taps on the menu itself must not bubble to the scrim (which closes).
              onStartShouldSetResponder={() => true}
            >
              <ScrollView style={{ maxHeight: 220 }}>
                {options.map(o => (
                  <Pressable key={o} onPress={() => { onChange(o); setOpen(false); }} style={ss.dropdownRow}>
                    <Text style={{ color: o === value ? colors.accent : colors.ink2, fontSize: 13 }}>{labelFor ? labelFor(o) : o}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          )}
        </Pressable>
      </Modal>
    </View>
  );
};

export default function SendTestScreen() {
  const { theme: { colors, font } } = useTheme();
  const { pad } = useResponsive();
  const toast = useToast();
  const t = useT();

  const [source, setSource] = useState('works');
  const [productId, setProductId] = useState('works');
  const [referenceId, setReferenceId] = useState('');
  const [template, setTemplate] = useState('payment-receipt');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState('en');
  const [channels, setChannels] = useState<Record<ChannelType, boolean>>({ email: true, whatsapp: false, sms: false, push: false });
  const [json, setJson] = useState(INITIAL_JSON);
  const [fallbacks, setFallbacks] = useState<FallbackRuleForm[]>([]);
  const [response, setResponse] = useState<{ kind: 'success' | 'duplicate' | 'error'; jobId?: string; message?: string } | null>(null);
  const [sending, setSending] = useState(false);

  const [locales, setLocales] = useState<string[]>(FALLBACK_LOCALES);
  const [templates, setTemplates] = useState<string[]>(['payment-receipt']);
  const [products, setProducts] = useState<string[]>(FALLBACK_PRODUCTS);
  const [activeChannels, setActiveChannels] = useState<Set<string> | null>(null);

  useEffect(() => { getLocales().then(d => { if (d.supported?.length) setLocales(d.supported); }).catch(() => {}); }, []);
  useEffect(() => { getTemplates().then(d => { if (d.templates?.length) setTemplates(d.templates); }).catch(() => {}); }, []);
  useEffect(() => { getAllUsage().then(d => { if (d.products?.length) setProducts(d.products.map(p => p.productId)); }).catch(() => {}); }, []);
  useEffect(() => { getHealth().then(d => setActiveChannels(new Set(d.activeChannels ?? []))).catch(() => {}); }, []);

  const isChannelEnabled = (ch: string) => (activeChannels ? activeChannels.has(ch) : ch === 'email');
  const jsonValid = useMemo(() => { try { JSON.parse(json); return true; } catch { return false; } }, [json]);
  const hasChannel = Object.values(channels).some(Boolean);
  const phoneRequired = channels.whatsapp || channels.sms;
  const phoneValid = !phoneRequired || /^\+\d{8,15}$/.test(phone.replace(/\s/g, ''));
  const canSubmit = !!email && !!name && hasChannel && jsonValid && phoneValid && !sending;

  const blockers: string[] = [];
  if (!name) blockers.push(t('sendTest.blockerName'));
  if (!email) blockers.push(t('sendTest.blockerEmail'));
  if (!hasChannel) blockers.push(t('sendTest.blockerChannel'));
  if (!jsonValid) blockers.push(t('sendTest.blockerJson'));
  if (!phoneValid) blockers.push(t('sendTest.blockerPhone'));

  const submit = async () => {
    if (!canSubmit) return;
    setSending(true);
    const active = (Object.entries(channels).filter(([, v]) => v).map(([key]) => key)) as ChannelType[];
    const payload: NotifyPayload = {
      metadata: { sourceModule: source, productId, referenceId: referenceId || `test_${Date.now()}` },
      recipient: { name, email: email || undefined, phone: phone || undefined, locale },
      templateId: template,
      templateData: JSON.parse(json),
      channels: active,
      fallbacks: fallbacks.map(toApiFallback),
      idempotencyKey: referenceId ? `${source}:${referenceId}` : undefined,
    };
    try {
      const data = await sendNotification(payload);
      if (data.status === 'duplicate_suppressed') {
        setResponse({ kind: 'duplicate', jobId: data.jobId });
      } else {
        setResponse({ kind: 'success', jobId: data.jobId });
        toast.success(t('sendTest.toastQueued'), { message: data.jobId });
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || (err as Error)?.message || 'Unknown error';
      setResponse({ kind: 'error', message: msg });
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setResponse(null); setReferenceId(''); };
  const inputStyle = [ss.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }];

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, alignItems: 'center' }}>
      <View style={[ss.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
        <Text style={{ color: colors.ink, fontSize: 20, fontWeight: '700' }}>{t('sendTest.title')}</Text>
        <Text style={{ color: colors.ink3, fontSize: 13, marginTop: 3, marginBottom: 16 }}>{t('sendTest.subtitle')}</Text>

        <View style={{ marginBottom: 14 }}>
          <Banner tone="warning" icon="alert">
            <Text style={{ color: colors.ink, fontSize: 13 }}><Text style={{ fontWeight: '700' }}>{t('sendTest.liveWarningTitle')}</Text>{t('sendTest.liveWarningBody')}</Text>
          </Banner>
        </View>

        {response && (
          <View style={{ marginBottom: 14 }}>
            {response.kind === 'success' && (
              <Banner tone="success" icon="check" onClose={reset}>
                <Text style={{ color: colors.ink, fontWeight: '600', fontSize: 13 }}>{t('sendTest.queuedTitle')}</Text>
                <Text style={{ color: colors.ink3, fontSize: 12, marginVertical: 4 }}>{t('sendTest.queuedBody')}</Text>
                <Copyable value={response.jobId!} />
              </Banner>
            )}
            {response.kind === 'duplicate' && (
              <Banner tone="warning" icon="info" onClose={reset}>
                <Text style={{ color: colors.ink, fontWeight: '600', fontSize: 13 }}>{t('sendTest.duplicateTitle')}</Text>
                <Text style={{ color: colors.ink3, fontSize: 12, marginVertical: 4 }}>{t('sendTest.duplicateBody')}</Text>
                <Copyable value={response.jobId!} />
              </Banner>
            )}
            {response.kind === 'error' && (
              <Banner tone="error" icon="alert" onClose={reset}>
                <Text style={{ color: colors.ink, fontWeight: '600', fontSize: 13 }}>{t('sendTest.errorTitle')}</Text>
                <Text style={{ color: colors.ink3, fontSize: 12, fontFamily: font.mono, marginTop: 4 }}>{response.message}</Text>
              </Banner>
            )}
          </View>
        )}

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Field label={t('sendTest.sourceModule')} required>
              <TextInput value={source} onChangeText={setSource} placeholder={t('sendTest.sourceModulePlaceholder')} placeholderTextColor={colors.ink4} style={inputStyle} />
            </Field>
          </View>
          <View style={{ flex: 1 }}>
            <Field label={t('sendTest.template')} required>
              <Selectish value={template} onChange={setTemplate} options={templates} />
            </Field>
          </View>
        </View>

        <Field label={t('sendTest.product')} required hint={t('sendTest.productHint')}>
          <Selectish value={productId} onChange={setProductId} options={products} />
        </Field>

        <Field label={t('sendTest.referenceId')} hint={t('sendTest.referenceIdHint', { source, ref: referenceId || 'auto-uuid' })}>
          <TextInput value={referenceId} onChangeText={setReferenceId} placeholder={t('sendTest.referenceIdPlaceholder')} placeholderTextColor={colors.ink4} style={[...inputStyle, { fontFamily: font.mono }]} />
        </Field>

        <Field label={t('sendTest.recipientName')} required>
          <TextInput value={name} onChangeText={setName} placeholder={t('sendTest.recipientNamePlaceholder')} placeholderTextColor={colors.ink4} style={inputStyle} />
        </Field>

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Field label={t('sendTest.email')} required>
              <TextInput value={email} onChangeText={setEmail} placeholder={t('sendTest.emailPlaceholder')} placeholderTextColor={colors.ink4} autoCapitalize="none" style={inputStyle} />
            </Field>
          </View>
          <View style={{ flex: 1 }}>
            <Field label={`${t('sendTest.phone')}${phoneRequired ? ' *' : ''}`} hint={t('sendTest.phoneHint')} hintError={phoneRequired && !phoneValid}>
              <TextInput value={phone} onChangeText={setPhone} placeholder={t('sendTest.phonePlaceholder')} placeholderTextColor={colors.ink4} style={inputStyle} />
            </Field>
          </View>
        </View>

        <Field label={t('sendTest.locale')}>
          <Selectish value={locale} onChange={setLocale} options={locales} labelFor={(c) => (LOCALE_LABELS[c] ? `${LOCALE_LABELS[c]} (${c})` : c)} width={200} />
        </Field>

        <Field label={t('sendTest.channels')} required hint={!hasChannel ? t('sendTest.channelsHint') : undefined} hintError={!hasChannel}>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {CHANNELS.map(({ key, icon }) => {
              const enabled = isChannelEnabled(key);
              const on = channels[key];
              return (
                <Pressable
                  key={key}
                  onPress={() => enabled && setChannels(c => ({ ...c, [key]: !c[key] }))}
                  style={[ss.pill, { borderColor: on ? colors.accent : colors.line, backgroundColor: on ? colors.accentSoft : colors.paper, opacity: enabled ? 1 : 0.45 }]}
                >
                  <Icon name={icon} size={14} color={on ? colors.accentInk : colors.ink3} />
                  <Text style={{ color: on ? colors.accentInk : colors.ink2, fontSize: 12, textTransform: 'capitalize' }}>{key}</Text>
                  {!enabled && <Text style={{ color: colors.ink4, fontSize: 10 }}>{t('sendTest.notConfigured')}</Text>}
                </Pressable>
              );
            })}
          </View>
        </Field>

        <Field label={t('sendTest.templateData')} required hint={!jsonValid ? t('sendTest.jsonInvalid') : undefined} hintError={!jsonValid}>
          <View style={[ss.jsonWrap, { borderColor: jsonValid ? colors.line : colors.danger, backgroundColor: colors.paper }]}>
            <View style={[ss.jsonHead, { borderBottomColor: colors.line }]}>
              <Text style={{ color: colors.ink4, fontSize: 10, fontFamily: font.mono }}>{t('sendTest.jsonContentType')}</Text>
              <Text style={{ color: jsonValid ? colors.success : colors.danger, fontSize: 10 }}>{jsonValid ? t('sendTest.jsonValid') : t('sendTest.jsonInvalidBadge')}</Text>
            </View>
            <TextInput value={json} onChangeText={setJson} multiline style={[ss.jsonInput, { color: colors.ink, fontFamily: font.mono }]} />
          </View>
        </Field>

        <Field label={t('sendTest.fallbackRules')}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={{ color: colors.ink4, fontSize: 11 }}>{t('sendTest.fallbackRulesHint')}</Text>
            <Btn label={t('sendTest.addRule')} icon="plus" size="sm" onPress={() => setFallbacks(r => [...r, { from: 'whatsapp', to: 'email', after: 5 }])} />
          </View>
          {fallbacks.map((r, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <Selectish value={r.from} onChange={v => setFallbacks(rs => rs.map((x, j) => (j === i ? { ...x, from: v as ChannelType } : x)))} options={['email', 'whatsapp', 'sms', 'push']} width={110} />
              <Text style={{ color: colors.ink3, fontSize: 12 }}>{t('sendTest.failsSendVia')}</Text>
              <Selectish value={r.to} onChange={v => setFallbacks(rs => rs.map((x, j) => (j === i ? { ...x, to: v as ChannelType } : x)))} options={['email', 'whatsapp', 'sms', 'push']} width={110} />
              <Text style={{ color: colors.ink3, fontSize: 12 }}>{t('sendTest.after')}</Text>
              <TextInput value={String(r.after)} onChangeText={v => setFallbacks(rs => rs.map((x, j) => (j === i ? { ...x, after: Number(v) || 0 } : x)))} keyboardType="numeric" style={[...inputStyle, { width: 56 }]} />
              <Text style={{ color: colors.ink3, fontSize: 12 }}>{t('sendTest.min')}</Text>
              <Pressable onPress={() => setFallbacks(rs => rs.filter((_, j) => j !== i))} hitSlop={6}><Icon name="trash" size={13} color={colors.ink3} /></Pressable>
            </View>
          ))}
        </Field>

        {!canSubmit && blockers.length > 0 && (
          <Text style={{ color: colors.ink3, fontSize: 12, textAlign: 'center', marginBottom: 8 }}>{t('sendTest.toSendAdd', { blockers: blockers.join(', ') })}</Text>
        )}
        <Btn label={sending ? t('sendTest.sending') : t('sendTest.send')} icon="send" kind="primary" size="lg" block disabled={!canSubmit} onPress={submit} />
      </View>
    </ScrollView>
  );
}

const ss = StyleSheet.create({
  card: { width: '100%', maxWidth: 720, padding: 24, borderRadius: 2, borderWidth: 1 },
  input: { height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, fontSize: 13, outlineStyle: 'none' as any },
  pill: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 2, borderWidth: 1 },
  jsonWrap: { borderWidth: 1, borderRadius: 2, overflow: 'hidden' },
  jsonHead: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10, paddingVertical: 6, borderBottomWidth: 1 },
  jsonInput: { padding: 10, fontSize: 12, minHeight: 150, textAlignVertical: 'top', outlineStyle: 'none' as any },
  dropdown: {
    borderWidth: 1, borderRadius: 2,
    // Rendered inside a Modal (anchored via measureInWindow) so it floats above
    // everything; elevation/shadow give it depth on native.
    elevation: 8,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12, shadowRadius: 12,
  },
  dropdownRow: { paddingHorizontal: 12, paddingVertical: 9 },
});
